import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { ConfigProvider, notification } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import {
    AgenticTwoPanelLayoutProps,
    AgenticChildAccordionItem,
    AgenticQuestion,
    AgenticPhaseCard,
    SuggestedAction,
} from '../AskAsperAgentic.types';
import { transformQuestionBankCategories } from '../utils/transformQuestionBankCategories';
import AgenticLeftPanel from '../LeftPanel/AgenticLeftPanel';
import AgenticRightPanel from '../RightPanel/AgenticRightPanel';
import AgenticFocusRightPanel from '../RightPanel/AgenticFocusRightPanel';
import AgenticTopBar from '../TopBar/AgenticTopBar';
import AgenticSidebar from '../Sidebar/AgenticSidebar';
import CreateCaseModal from '../Modals/CreateCaseModal';
import { CreateCaseSaveData } from '../Modals/CreateCaseModal.types';
import AgenticQuestionBank from '../QuestionBank/AgenticQuestionBank';
import Modal from '../../../atoms/Modal/Modal';
import Tooltip from '../../../atoms/Tooltip/Tooltip';
import LayoutLeft from '../../../atoms/Icon/LayoutLeft';
import Menu from '../../../atoms/Icon/Menu';
import { deriveLayoutFromMessages } from './deriveLayoutFromMessages';
import { scrollElementToStartWithinContainer } from '../utils/scrollWithinContainer';
import { wrapFeedbackHandlersWithSuccessToast } from '../utils/wrapFeedbackHandlersWithSuccessToast';
import {
    createChartEventForwarder,
    createMixpanelEventFirer,
    getTextLength,
    CONVERSATION_PANEL_VIEW,
    THREAD_PANEL_VIEW,
    THREAD_PANEL_THREAD_CLICK,
    THREAD_PANEL_THREAD_EDIT,
    THREAD_PANEL_THREAD_DELETE,
    THREAD_PANEL_SEARCH,
    THREAD_PANEL_SEARCH_CLOSE,
    THREAD_PANEL_NEW_CHAT_CLICK,
    QUESTION_BANK_VIEW,
    QUESTION_BANK_SECTION_CLICK,
    QUESTION_BANK_QUESTION_CLICK,
    QUESTION_BANK_SAVED_QUESTION_EDIT,
    QUESTION_BANK_SAVED_QUESTION_DELETE,
    QUESTION_BANK_SEARCH,
    QUESTION_BANK_SEARCH_CLOSE,
    LANDING_PAGE_SEND_CLICK,
    LANDING_PAGE_SAVED_QUESTION_CLICK,
    LANDING_PAGE_SAVED_QUESTION_SEND,
    SAVE_AS_CUSTOM,
    LEFT_PANEL_HIDE_CLICK,
    LEFT_PANEL_RESIZE,
    LEFT_PANEL_VIEW_ARTEFACT_CLICK,
    LEFT_PANEL_SHOW_TASK_CLICK,
    LEFT_PANEL_FOLLOW_UP_PILL_CLICK,
    RIGHT_PANEL_PRESENTATION_MODE_CLICK,
    RIGHT_PANEL_LIST_MODE_CLICK,
    RIGHT_PANEL_PRESENTATION_ARROW_CLICK,
    RIGHT_PANEL_METHODOLOGY_EXPAND_CLICK,
    RIGHT_PANEL_METHODOLOGY_COLLAPSE_CLICK,
    RIGHT_PANEL_ARTEFACT_EXPORT_CLICK,
    RIGHT_PANEL_MULTI_SELECT_CLICK,
    RIGHT_PANEL_MULTI_SELECT_SELECT_ALL_CLICK,
    RIGHT_PANEL_MULTI_SELECT_DESELECT_ALL_CLICK,
    RIGHT_PANEL_MULTI_SELECT_EXIT_CLICK,
    RIGHT_PANEL_MULTI_SELECT_ARTEFACT_TOGGLE,
    RIGHT_PANEL_MULTI_SELECT_EXPORT_OPTION_CLICK,
    RIGHT_PANEL_ARTEFACT_EXPORT_OPTION_CLICK,
    RIGHT_PANEL_SUMMARY_CITATION_CLICK,
    RIGHT_PANEL_ARTEFACT_THUMBS_UP_CLICK,
    RIGHT_PANEL_ARTEFACT_THUMBS_DOWN_CLICK,
    RIGHT_PANEL_ARTEFACT_FEEDBACK_TEXT_SUBMIT,
} from '../utils/mixpanelEvents';
import {
    fireQuerySendWithSlashFilters,
    queryHasSlashFilters,
} from '../utils/slashCommandMixpanel';

// Same default saved questions as AgenticLandingContent
const DEFAULT_SAVED_QUESTIONS: AgenticQuestion[] = [
    {
        id: 'custom1',
        text: 'What is the base vs incremental sales split for top brands in the category in last 52 weeks?',
    },
    { id: 'custom2', text: 'How does my brand pricing compare to competitors across different retailers?' },
    { id: 'custom3', text: 'Which products are gaining market share in the last quarter?' },
    { id: 'custom4', text: 'What is the price index trend over time for my portfolio?' },
];

/**
 * AgenticTwoPanelLayout - Two-panel layout with resizable divider
 *
 * Features:
 * - Left panel: Conversation, phase cards with subtasks, HITL cards
 * - Right panel: Artifacts (visualizations) and summary
 * - Horizontal resizable divider between panels
 * - Responsive design
 * - Optional top bar
 */
const AgenticTwoPanelLayout: React.FC<AgenticTwoPanelLayoutProps> = ({
    // Messages-driven mode (derives left/right panel from messages + loadingMessage)
    messages,
    loadingMessage,
    onSendMessage,
    onStopGenerating,

    // Left Panel Props (used when messages not provided)
    leftPanelHeaderTitle,
    onLeftPanelHeaderAction,
    leftPanelTimestamp,
    conversationMessages = [],
    runningTasks = [],
    plannerTasks,
    phaseCards = [],
    onTogglePhase,
    hitlCards = [],
    onViewArtefact,
    onEntityDisambiguationContinue,
    onEntityDisambiguationSkip,
    assessmentStatus,
    assessmentContent,
    suggestedActions = [],

    // Legacy support (deprecated)
    analysisSteps = [],
    phaseCompleteStatus,
    showTasks = true,
    onToggleTasks,

    // Right Panel Props
    accordionItems = [],
    artifacts = [],
    activeArtifactId,
    summary,
    onArtifactSelect,
    onArtifactClose,
    isMultiSelectMode = false,
    onToggleMultiSelect,
    selectedArtifactIds = [],
    onArtifactSelectionChange,
    onRightPanelClose,
    onRightPanelThumbsUp,
    onRightPanelThumbsDown,
    onRightPanelDislikeFeedbackSubmit,
    feedbackHistory,

    // Layout Configuration
    defaultLeftPanelWidth = 35,
    minLeftPanelWidth = 30,
    maxLeftPanelWidth = 70,
    onPanelResize,
    footerHeight = 0,

    // Top Bar Props
    showTopBar = true,
    disableNavbar = false,
    breadcrumbs,
    onBreadcrumbClick,
    onHomeClick,
    onNotificationClick,
    showNotification = true,
    notificationCount = 0,

    // Sidebar Props
    showSidebar = false,
    sidebarCollapsed: controlledSidebarCollapsed,
    onSidebarToggle,
    sidebarThreads = [],
    sidebarThreadsLoading = false,
    sidebarActiveThreadId,
    sidebarUser = { name: 'User' },
    onSidebarNewChat,
    onSidebarSearchChats,
    onSidebarThreadSelect,
    onSidebarThreadEdit,
    onSidebarThreadDelete,
    onSidebarUserClick,

    // View mode
    viewMode = 'list',
    onViewModeChange,
    showViewModeToggle = false,

    // Action buttons
    onCommentClick,
    onCreateCaseClick,
    showCommentButton = false,
    showCreateCaseButton = false,
    // Create Case Modal props
    existingCases = [],
    onCaseSave,

    // DotsGrid / Tool Panel
    onDotsGridClick,
    showDotsGridButton = false,
    toolPanelOpen,
    onToolPanelOpenChange,
    onExportClick,
    onArtifactExport,
    onMultiSelectExport,
    onShareClick,
    onPlaceholderClick,
    onToolPanelSearch,
    onQuestionBankClick,
    onSaveAsCustom,
    inputDisabled = false,
    questionBankCategories,
    customQuestions,
    onAddCustomQuestion,
    onEditCustomQuestion,
    onDeleteCustomQuestion,
    searchDisclaimer,

    // Slash-command lookup integration
    lookupItems,
    selectedLookups,
    onLookupsChange,
    onLoadLookups,
    lookupCategories,
    lookupDimensionGroups,

    // Mixpanel Analytics
    onTrackEvent,

    exportDeepLink,
    exportThreadId,
    exportDeepLinkTarget,
    onExportDeepLinkConsumed,

    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    // ===== Mixpanel: safe fire helper (no-ops when onTrackEvent is not provided) =====
    const fireEvent = useMemo(() => createMixpanelEventFirer(onTrackEvent), [onTrackEvent]);
    const analyticsSessionIdRef = useRef(
        typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `session-${Date.now()}`,
    );
    const slashCommandAnalyticsContext = useMemo(
        () => ({
            threadId: sidebarActiveThreadId,
            sessionId: analyticsSessionIdRef.current,
        }),
        [sidebarActiveThreadId],
    );
    /** Stable forwarder mapping `ChartTrackEvent` → AskAsper `RIGHT_PANEL_CHART_*` mixpanel events. */
    const chartEventForwarder = useMemo(() => createChartEventForwarder(fireEvent), [fireEvent]);

    const [leftPanelWidth, setLeftPanelWidth] = useState(defaultLeftPanelWidth);
    const [isResizing, setIsResizing] = useState(false);
    const [focusPanelActiveIndex, setFocusPanelActiveIndex] = useState(0);
    const [internalSidebarCollapsed, setInternalSidebarCollapsed] = useState(true);
    /** When in focus/presentation mode: whether the left panel is expanded (true) or collapsed (false). LayoutLeft toggles this. */
    const [focusModeLeftPanelExpanded, setFocusModeLeftPanelExpanded] = useState(false);
    /** When in list mode: whether the left panel is collapsed (true) or expanded (false). LayoutLeft toggle in panel header controls this. */
    const [listModeLeftPanelCollapsed, setListModeLeftPanelCollapsed] = useState(false);
    const [createCaseModalOpen, setCreateCaseModalOpen] = useState(false);
    const [isQuestionBankOpen, setIsQuestionBankOpen] = useState(false);
    const [savedQuestions, setSavedQuestions] = useState<AgenticQuestion[]>(DEFAULT_SAVED_QUESTIONS);
    const [layoutInputValue, setLayoutInputValue] = useState('');

    // Mixpanel: track the most recent saved-question text so we can fire a saved-question send
    // when the user submits it unedited. Cleared when the user edits the input.
    const lastSavedQuestionTextRef = useRef<string | undefined>(undefined);

    // Use customQuestions from parent when provided (for API persistence), else internal state
    const effectiveSavedQuestions = customQuestions ?? savedQuestions;

    // Configure notifications to appear from bottom-right when export is triggered
    useEffect(() => {
        notification.config({
            placement: 'bottomRight',
            bottom: 24,
            top: 24,
        });
    }, []);

    // Mixpanel: Fire conversation panel view event on mount (with thread_id when available)
    useEffect(() => {
        fireEvent(CONVERSATION_PANEL_VIEW, sidebarActiveThreadId ? { thread_id: sidebarActiveThreadId } : {});
        // Fire only once per mount — thread_id is captured at first render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reset focus mode left panel state when switching out of presentation mode
    useEffect(() => {
        if (viewMode !== 'presentation') {
            setFocusModeLeftPanelExpanded(false);
        }
    }, [viewMode]);

    // Reset list mode left panel to expanded when switching to list mode
    useEffect(() => {
        if (viewMode === 'list') {
            setListModeLeftPanelCollapsed(false);
        }
    }, [viewMode]);

    const containerRef = useRef<HTMLDivElement>(null);
    const twoPanelContainerRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);
    const rightPanelRef = useRef<HTMLDivElement>(null);

    // ===== Mixpanel: wrapped right-panel feedback callbacks (declared before feedbackHandlersForLayout to avoid TDZ) =====
    const handleRightPanelThumbsUp = useMemo(
        () =>
            onRightPanelThumbsUp
                ? (payload: import('../AskAsperAgentic.types').RightPanelFeedbackPayload) => {
                      const artefactName = payload.kind === 'task' ? payload.taskTitle : payload.questionTitle;
                      fireEvent(RIGHT_PANEL_ARTEFACT_THUMBS_UP_CLICK, { artefact_name: artefactName });
                      onRightPanelThumbsUp(payload);
                  }
                : undefined,
        [onRightPanelThumbsUp, fireEvent],
    );

    const handleRightPanelThumbsDown = useMemo(
        () =>
            onRightPanelThumbsDown
                ? (payload: import('../AskAsperAgentic.types').RightPanelFeedbackPayload) => {
                      const artefactName = payload.kind === 'task' ? payload.taskTitle : payload.questionTitle;
                      fireEvent(RIGHT_PANEL_ARTEFACT_THUMBS_DOWN_CLICK, { artefact_name: artefactName });
                      onRightPanelThumbsDown(payload);
                  }
                : undefined,
        [onRightPanelThumbsDown, fireEvent],
    );

    const handleRightPanelDislikeFeedbackSubmit = useMemo(
        () =>
            onRightPanelDislikeFeedbackSubmit
                ? (payload: import('../AskAsperAgentic.types').RightPanelDislikeFeedbackPayload) => {
                      const artefactName = payload.kind === 'task' ? payload.taskTitle : payload.questionTitle;
                      fireEvent(RIGHT_PANEL_ARTEFACT_FEEDBACK_TEXT_SUBMIT, {
                          artefact_name: artefactName,
                          feedback_text_length: getTextLength(payload.feedback),
                          feedback_attachment_count: payload.files?.length ?? 0,
                      });
                      onRightPanelDislikeFeedbackSubmit(payload);
                  }
                : undefined,
        [onRightPanelDislikeFeedbackSubmit, fireEvent],
    );

    const feedbackHandlersForLayout = useMemo(
        () =>
            wrapFeedbackHandlersWithSuccessToast({
                onRightPanelThumbsUp: handleRightPanelThumbsUp,
                onRightPanelThumbsDown: handleRightPanelThumbsDown,
                onRightPanelDislikeFeedbackSubmit: handleRightPanelDislikeFeedbackSubmit,
            }),
        [handleRightPanelThumbsUp, handleRightPanelThumbsDown, handleRightPanelDislikeFeedbackSubmit],
    );

    // When messages/loadingMessage are provided, derive left/right panel state from them
    const derivedState = useMemo(() => {
        if (messages == null) return null;
        return deriveLayoutFromMessages(messages, loadingMessage, feedbackHandlersForLayout, feedbackHistory);
    }, [messages, loadingMessage, feedbackHandlersForLayout, feedbackHistory]);

    const useMessagesMode = derivedState != null;

    const effectiveConversationPairs = useMessagesMode ? derivedState!.conversationPairs : undefined;
    const effectiveConversationMessages = useMessagesMode ? undefined : conversationMessages;
    const effectiveRunningTasks = useMessagesMode ? derivedState!.runningTasks : runningTasks;
    const effectivePlannerTasks = useMessagesMode ? undefined : plannerTasks;
    const effectivePhaseCards = useMessagesMode ? undefined : phaseCards;
    const effectiveRightPanelBlocks = useMessagesMode ? derivedState!.rightPanelBlocks : undefined;
    const effectiveAccordionItems = useMessagesMode ? undefined : accordionItems;
    const effectiveSummary = useMessagesMode ? undefined : summary;
    const effectiveLeftPanelTimestamp = useMessagesMode ? derivedState!.leftPanelTimestamp : leftPanelTimestamp;

    // Derive conversation title: use prop if provided, else from active sidebar thread
    const effectiveLeftPanelHeaderTitle = useMemo(() => {
        if (leftPanelHeaderTitle) return leftPanelHeaderTitle;
        if (sidebarActiveThreadId && sidebarThreads?.length) {
            const active = sidebarThreads.find((t) => t.thread_id === sidebarActiveThreadId);
            return active?.thread_name;
        }
        return undefined;
    }, [leftPanelHeaderTitle, sidebarActiveThreadId, sidebarThreads]);

    // Log props that populate left and right panel contents
    useEffect(() => {
        console.log('[AgenticTwoPanelLayout] Left panel props:', {
            effectiveLeftPanelTimestamp,
            effectiveConversationMessages,
            effectiveConversationPairs,
            effectiveRunningTasks,
            effectivePlannerTasks,
            effectivePhaseCards,
            hitlCards,
            showTasks,
            useMessagesMode,
        });
    }, [
        effectiveLeftPanelTimestamp,
        effectiveConversationMessages,
        effectiveConversationPairs,
        effectiveRunningTasks,
        effectivePlannerTasks,
        effectivePhaseCards,
        hitlCards,
        showTasks,
        useMessagesMode,
    ]);

    useEffect(() => {
        console.log('[AgenticTwoPanelLayout] Right panel props:', {
            viewMode,
            effectiveRightPanelBlocks,
            effectiveAccordionItems,
            artifacts,
            activeArtifactId,
            effectiveSummary,
            flattenedItems: viewMode === 'presentation' ? flattenedItems : undefined,
        });
    }, [viewMode, effectiveRightPanelBlocks, effectiveAccordionItems, artifacts, activeArtifactId, effectiveSummary]);

    // Sidebar collapsed state (controlled or uncontrolled)
    const isSidebarCollapsed =
        controlledSidebarCollapsed !== undefined ? controlledSidebarCollapsed : internalSidebarCollapsed;

    // Handle sidebar toggle from menu button or sidebar internal toggle
    const handleSidebarToggle = useCallback(
        (collapsed: boolean) => {
            // Mixpanel: Fire thread-panel view event when sidebar opens (collapsed -> expanded)
            if (!collapsed) {
                fireEvent(THREAD_PANEL_VIEW);
            }
            if (controlledSidebarCollapsed === undefined) {
                setInternalSidebarCollapsed(collapsed);
            }
            onSidebarToggle?.(collapsed);
        },
        [controlledSidebarCollapsed, onSidebarToggle, fireEvent],
    );

    // Handle menu toggle button click (toggles sidebar)
    const handleMenuToggle = useCallback(() => {
        handleSidebarToggle(!isSidebarCollapsed);
    }, [handleSidebarToggle, isSidebarCollapsed]);

    // Handle create case button click
    const handleCreateCaseClick = useCallback(() => {
        setCreateCaseModalOpen(true);
        onCreateCaseClick?.();
    }, [onCreateCaseClick]);

    // Handle create case modal save
    const handleCaseSave = useCallback(
        (data: CreateCaseSaveData) => {
            onCaseSave?.(data);
            setCreateCaseModalOpen(false);
        },
        [onCaseSave],
    );

    // Handle create case modal cancel
    const handleCaseModalCancel = useCallback(() => {
        setCreateCaseModalOpen(false);
    }, []);

    // ===== Multi-select state (internal when not controlled by parent) =====
    const [internalIsMultiSelectMode, setInternalIsMultiSelectMode] = useState(false);
    const [internalSelectedArtifactIds, setInternalSelectedArtifactIds] = useState<string[]>([]);

    const effectiveIsMultiSelectMode =
        onToggleMultiSelect !== undefined ? isMultiSelectMode : internalIsMultiSelectMode;
    const effectiveSelectedArtifactIds =
        onArtifactSelectionChange !== undefined ? selectedArtifactIds : internalSelectedArtifactIds;

    const handleToggleMultiSelect = useCallback(() => {
        // Mixpanel: fire enter (click) when entering, exit when leaving
        fireEvent(effectiveIsMultiSelectMode ? RIGHT_PANEL_MULTI_SELECT_EXIT_CLICK : RIGHT_PANEL_MULTI_SELECT_CLICK);
        if (onToggleMultiSelect) {
            onToggleMultiSelect();
        } else {
            setInternalIsMultiSelectMode((prev) => !prev);
            setInternalSelectedArtifactIds([]);
        }
    }, [onToggleMultiSelect, effectiveIsMultiSelectMode, fireEvent]);

    const handleArtifactSelectionChange = useCallback(
        (artifactId: string, selected: boolean) => {
            fireEvent(RIGHT_PANEL_MULTI_SELECT_ARTEFACT_TOGGLE, { artefact_title: artifactId, selected });
            if (onArtifactSelectionChange) {
                onArtifactSelectionChange(artifactId, selected);
            } else {
                setInternalSelectedArtifactIds((prev) =>
                    selected ? [...prev, artifactId] : prev.filter((id) => id !== artifactId),
                );
            }
        },
        [onArtifactSelectionChange, fireEvent],
    );

    const handleSelectAllArtifacts = useCallback(
        (newlySelectedIds: string[]) => {
            fireEvent(RIGHT_PANEL_MULTI_SELECT_SELECT_ALL_CLICK, {
                selected_count: newlySelectedIds.length,
            });
        },
        [fireEvent],
    );

    const handleDeselectAllArtifacts = useCallback(
        (clearedIds: string[]) => {
            fireEvent(RIGHT_PANEL_MULTI_SELECT_DESELECT_ALL_CLICK, {
                cleared_count: clearedIds.length,
            });
        },
        [fireEvent],
    );

    // ===== Mixpanel: View Methodology expand / collapse =====
    const handleMethodologyExpandChange = useCallback(
        (expanded: boolean, artefactTitle?: string) => {
            fireEvent(expanded ? RIGHT_PANEL_METHODOLOGY_EXPAND_CLICK : RIGHT_PANEL_METHODOLOGY_COLLAPSE_CLICK, {
                artefact_name: artefactTitle,
            });
        },
        [fireEvent],
    );

    // ===== Mixpanel: artefact-level export menu trigger click =====
    const handleArtefactMenuTriggerClick = useCallback(
        (artefactTitle: string) => {
            fireEvent(RIGHT_PANEL_ARTEFACT_EXPORT_CLICK, {
                artefact_name: artefactTitle,
            });
        },
        [fireEvent],
    );

    // Question Bank handlers (same as AgenticLandingContent)
    const handleQuestionBankOpen = useCallback(() => {
        fireEvent(QUESTION_BANK_VIEW);
        setIsQuestionBankOpen(true);
        onQuestionBankClick?.();
    }, [onQuestionBankClick, fireEvent]);

    const handleQuestionSelect = useCallback(
        (question: AgenticQuestion, categoryId: string) => {
            if (categoryId === 'saved') {
                fireEvent(LANDING_PAGE_SAVED_QUESTION_CLICK, {
                    question_id: question.id,
                    question_title: question.text,
                    thread_id: sidebarActiveThreadId,
                });
                lastSavedQuestionTextRef.current = question.text;
            } else {
                fireEvent(QUESTION_BANK_QUESTION_CLICK, {
                    question_id: question.id,
                    question_title: question.text,
                });
            }
            setLayoutInputValue(question.text);
            setIsQuestionBankOpen(false);
        },
        [fireEvent, sidebarActiveThreadId],
    );

    // Clear the saved-question marker when the user edits the input away from the saved-question text
    const handleLayoutInputChange = useCallback((value: string) => {
        if (lastSavedQuestionTextRef.current && lastSavedQuestionTextRef.current !== value) {
            lastSavedQuestionTextRef.current = undefined;
        }
        setLayoutInputValue(value);
    }, []);

    const handleEditSavedQuestion = useCallback(
        (question: { id?: string | number; text?: string; QuestionID?: number; Question?: string }) => {
            const questionId = question.id ?? question.QuestionID;
            const newText = question.text ?? question.Question ?? '';
            const oldText = savedQuestions.find((q) => String(q.id) === String(questionId))?.text;
            fireEvent(QUESTION_BANK_SAVED_QUESTION_EDIT, {
                question_id: questionId,
                old_question_title: oldText,
                new_question_title: newText,
            });
            onEditCustomQuestion?.(question);
            if (customQuestions == null) {
                setSavedQuestions((prev) =>
                    prev.map((q) => (String(q.id) === String(questionId) ? { ...q, text: newText } : q)),
                );
            }
        },
        [customQuestions, onEditCustomQuestion, fireEvent, savedQuestions],
    );

    const handleDeleteSavedQuestion = useCallback(
        (questionId: string) => {
            const deletedTitle = savedQuestions.find((q) => q.id === questionId)?.text;
            fireEvent(QUESTION_BANK_SAVED_QUESTION_DELETE, {
                question_id: questionId,
                question_title: deletedTitle,
            });
            onDeleteCustomQuestion?.(questionId);
            if (customQuestions == null) {
                setSavedQuestions((prev) => prev.filter((q) => q.id !== questionId));
            }
        },
        [customQuestions, onDeleteCustomQuestion, fireEvent, savedQuestions],
    );

    const handleSaveAsCustomClick = useCallback(
        (value: string) => {
            fireEvent(SAVE_AS_CUSTOM, {
                question_title: value,
                thread_id: sidebarActiveThreadId,
            });
            const newQuestion: AgenticQuestion = { id: `custom-${Date.now()}`, text: value };
            onAddCustomQuestion?.(newQuestion);
            if (customQuestions == null) {
                setSavedQuestions((prev) => [...prev, newQuestion]);
            }
            onSaveAsCustom?.(value);
        },
        [customQuestions, onAddCustomQuestion, onSaveAsCustom, fireEvent, sidebarActiveThreadId],
    );

    // Wrapped onInputSubmit to clear input after send (when in messages mode)
    const handleInputSubmit = useCallback(
        (value: string) => {
            const isSavedQuestion = !!lastSavedQuestionTextRef.current && lastSavedQuestionTextRef.current === value;
            fireEvent(LANDING_PAGE_SEND_CLICK, {
                ...(sidebarActiveThreadId ? { thread_id: sidebarActiveThreadId } : {}),
                source: isSavedQuestion ? 'saved_question' : 'typed',
                question_asked: value.trim(),
                question_text_length: getTextLength(value),
            });
            if (isSavedQuestion) {
                fireEvent(LANDING_PAGE_SAVED_QUESTION_SEND, {
                    question_title: value,
                    thread_id: sidebarActiveThreadId,
                    question_text_length: getTextLength(value),
                });
            }
            if (queryHasSlashFilters(value, selectedLookups)) {
                fireQuerySendWithSlashFilters(fireEvent, value, slashCommandAnalyticsContext);
            }
            lastSavedQuestionTextRef.current = undefined;
            onSendMessage?.(value);
            setLayoutInputValue('');
        },
        [onSendMessage, fireEvent, sidebarActiveThreadId, selectedLookups, slashCommandAnalyticsContext],
    );

    // Handle View Artefact: scroll right panel to artifact with matching task_id, then call parent callback
    const handleViewArtefact = useCallback(
        (artefactId: string, subtaskId: string) => {
            fireEvent(LEFT_PANEL_VIEW_ARTEFACT_CLICK, {
                artefact_name: artefactId,
                task_name: subtaskId,
            });
            const scrollContainer = rightPanelRef.current?.querySelector<HTMLElement>('[data-agentic-right-panel]');
            const el = scrollContainer?.querySelector<HTMLElement>(`[data-task-id="${artefactId}"]`);
            if (el && scrollContainer) {
                scrollElementToStartWithinContainer(el, scrollContainer, 'smooth');
            }
            onViewArtefact?.(artefactId, subtaskId);
        },
        [onViewArtefact, fireEvent],
    );

    // Flatten accordion items to get all items with content for the focus panel
    // This extracts both direct items with content and children from group items
    const flattenedItems = useMemo((): AgenticChildAccordionItem[] => {
        const items: AgenticChildAccordionItem[] = [];
        const sourceAccordionItems =
            effectiveRightPanelBlocks != null
                ? effectiveRightPanelBlocks.flatMap((b) => b.accordionItems)
                : (effectiveAccordionItems ?? []);

        sourceAccordionItems.forEach((item) => {
            if (item.children && item.children.length > 0) {
                // Group accordion - add all children
                items.push(...item.children);
            } else if (item.content) {
                // Regular accordion with content - convert to child format
                items.push({
                    id: item.id,
                    run_id: item.run_id,
                    session_id: item.session_id,
                    title: item.title,
                    defaultExpanded: item.defaultExpanded,
                    content: item.content,
                    onThumbsDown: item.onThumbsDown,
                    onThumbsUp: item.onThumbsUp,
                    onDislikeFeedbackSubmit: item.onDislikeFeedbackSubmit,
                    dislikeFeedbackContextTitle: item.dislikeFeedbackContextTitle,
                });
            }
        });

        return items;
    }, [effectiveRightPanelBlocks, effectiveAccordionItems]);

    // Mixpanel: track previous resizing state so we can fire LEFT_PANEL_RESIZE on completion only
    const prevIsResizingRef = useRef(false);
    useEffect(() => {
        if (prevIsResizingRef.current && !isResizing) {
            fireEvent(LEFT_PANEL_RESIZE, { panel_width: Math.round(leftPanelWidth) });
        }
        prevIsResizingRef.current = isResizing;
    }, [isResizing, leftPanelWidth, fireEvent]);

    // ===== Mixpanel: wrapped handlers for top-bar / sidebar / right-panel callbacks =====
    const handleViewModeChange = useCallback(
        (mode: 'list' | 'presentation') => {
            fireEvent(mode === 'presentation' ? RIGHT_PANEL_PRESENTATION_MODE_CLICK : RIGHT_PANEL_LIST_MODE_CLICK);
            onViewModeChange?.(mode);
        },
        [onViewModeChange, fireEvent],
    );

    const handlePresentationActiveIndexChange = useCallback(
        (nextIndex: number) => {
            const direction = nextIndex > focusPanelActiveIndex ? 'right' : 'left';
            const currentArtefact = flattenedItems[focusPanelActiveIndex];
            fireEvent(RIGHT_PANEL_PRESENTATION_ARROW_CLICK, {
                direction,
                artefact_name: currentArtefact?.title,
            });
            setFocusPanelActiveIndex(nextIndex);
        },
        [focusPanelActiveIndex, flattenedItems, fireEvent],
    );

    const handleSidebarThreadSelect = useCallback(
        (threadId: string) => {
            const thread = sidebarThreads.find((t) => t.thread_id === threadId);
            fireEvent(THREAD_PANEL_THREAD_CLICK, {
                thread_id: threadId,
                thread_title: thread?.thread_name,
            });
            onSidebarThreadSelect?.(threadId);
        },
        [onSidebarThreadSelect, sidebarThreads, fireEvent],
    );

    /** Deep link from PDF export: switch thread via host callback, then scroll in the right panel. */
    const deepLinkThreadSelectRequestedRef = useRef<string | null>(null);
    useEffect(() => {
        if (!exportDeepLinkTarget) {
            deepLinkThreadSelectRequestedRef.current = null;
            return;
        }

        const { threadId } = exportDeepLinkTarget;
        if (sidebarActiveThreadId === threadId) {
            return;
        }

        const requestKey = `${exportDeepLinkTarget.threadId}:${exportDeepLinkTarget.taskId ?? exportDeepLinkTarget.runId}`;
        if (deepLinkThreadSelectRequestedRef.current === requestKey) {
            return;
        }

        deepLinkThreadSelectRequestedRef.current = requestKey;
        handleSidebarThreadSelect(threadId);
    }, [exportDeepLinkTarget, sidebarActiveThreadId, handleSidebarThreadSelect]);

    const deepLinkTargetActive = exportDeepLinkTarget && sidebarActiveThreadId === exportDeepLinkTarget.threadId;
    const activeDeepLinkTaskId = deepLinkTargetActive ? exportDeepLinkTarget?.taskId : undefined;
    const activeDeepLinkRunId =
        deepLinkTargetActive && !exportDeepLinkTarget?.taskId ? exportDeepLinkTarget?.runId : undefined;

    const handleDeepLinkTaskScrollComplete = useCallback(
        (taskId: string, found: boolean) => {
            if (!exportDeepLinkTarget || exportDeepLinkTarget.taskId !== taskId) {
                return;
            }
            onExportDeepLinkConsumed?.(exportDeepLinkTarget, found);
        },
        [exportDeepLinkTarget, onExportDeepLinkConsumed],
    );

    const handleDeepLinkRunScrollComplete = useCallback(
        (runId: string, found: boolean) => {
            if (!exportDeepLinkTarget || exportDeepLinkTarget.runId !== runId) {
                return;
            }
            onExportDeepLinkConsumed?.(exportDeepLinkTarget, found);
        },
        [exportDeepLinkTarget, onExportDeepLinkConsumed],
    );

    const handleSidebarThreadEdit = useCallback(
        (threadId: string, newName: string) => {
            const oldName = sidebarThreads.find((t) => t.thread_id === threadId)?.thread_name;
            fireEvent(THREAD_PANEL_THREAD_EDIT, {
                thread_id: threadId,
                old_thread_title: oldName,
                new_thread_title: newName,
            });
            onSidebarThreadEdit?.(threadId, newName);
        },
        [onSidebarThreadEdit, sidebarThreads, fireEvent],
    );

    const handleSidebarThreadDelete = useCallback(
        (threadId: string) => {
            const thread = sidebarThreads.find((t) => t.thread_id === threadId);
            fireEvent(THREAD_PANEL_THREAD_DELETE, {
                thread_id: threadId,
                thread_title: thread?.thread_name,
            });
            onSidebarThreadDelete?.(threadId);
        },
        [onSidebarThreadDelete, sidebarThreads, fireEvent],
    );

    const handleSidebarSearchChats = useCallback(() => {
        // Spec: pass length only (privacy-first); the sidebar's onSearchChats fires on submit/run
        fireEvent(THREAD_PANEL_SEARCH);
        onSidebarSearchChats?.();
    }, [onSidebarSearchChats, fireEvent]);

    const handleSidebarSearchChatsClear = useCallback(() => {
        fireEvent(THREAD_PANEL_SEARCH_CLOSE);
    }, [fireEvent]);

    const handleQuestionBankSearch = useCallback(
        (value: string) => {
            fireEvent(QUESTION_BANK_SEARCH, { search_text_length: getTextLength(value) });
        },
        [fireEvent],
    );

    const handleQuestionBankSearchClear = useCallback(() => {
        fireEvent(QUESTION_BANK_SEARCH_CLOSE);
    }, [fireEvent]);

    const handleQuestionBankSectionClick = useCallback(
        (_sectionId: string, sectionName: string) => {
            fireEvent(QUESTION_BANK_SECTION_CLICK, { section_name: sectionName });
        },
        [fireEvent],
    );

    // ===== Mixpanel: left-panel phase card "Show Task" toggle =====
    const handlePhaseToggleCollapsed = useCallback(
        (collapsed: boolean, phase: AgenticPhaseCard) => {
            fireEvent(LEFT_PANEL_SHOW_TASK_CLICK, {
                phase_id: phase.id,
                phase_title: phase.title,
                action: collapsed ? 'hide' : 'show',
            });
        },
        [fireEvent],
    );

    // ===== Mixpanel: left-panel follow-up suggestion pill click =====
    const handleSuggestedActionClick = useCallback(
        (action: SuggestedAction) => {
            fireEvent(LEFT_PANEL_FOLLOW_UP_PILL_CLICK, {
                action_id: action.id,
                action_label: action.label,
                question_text_length: getTextLength(action.question),
            });
        },
        [fireEvent],
    );

    const handleSidebarNewChat = useCallback(() => {
        fireEvent(THREAD_PANEL_NEW_CHAT_CLICK);
        onSidebarNewChat?.();
    }, [onSidebarNewChat, fireEvent]);

    // ===== Mixpanel: wrapped left-panel hide buttons (focus + list mode collapse buttons) =====
    const handleHideLeftPanelInFocusMode = useCallback(() => {
        fireEvent(LEFT_PANEL_HIDE_CLICK, { mode: 'presentation' });
        setFocusModeLeftPanelExpanded(false);
    }, [fireEvent]);

    const handleHideLeftPanelInListMode = useCallback(() => {
        fireEvent(LEFT_PANEL_HIDE_CLICK, { mode: 'list' });
        setListModeLeftPanelCollapsed(true);
    }, [fireEvent]);

    // ===== Mixpanel: export-option-click tracking =====
    // Fired at the moment a specific export option is chosen (covers every option: Word / PDF / PPT /
    // Excel variants), independent of whether the export runs client-side or via the host callback.
    const handleArtefactExportOptionClick = useCallback(
        (exportOption: string, artefactName: string) => {
            fireEvent(RIGHT_PANEL_ARTEFACT_EXPORT_OPTION_CLICK, {
                artefact_name: artefactName,
                export_option: exportOption,
            });
        },
        [fireEvent],
    );

    const handleSummaryCitationClick = useCallback(
        (taskId: string, serial: number) => {
            fireEvent(RIGHT_PANEL_SUMMARY_CITATION_CLICK, {
                task_name: taskId,
                task_serial: serial,
            });
        },
        [fireEvent],
    );

    const handleMultiSelectExportOptionClick = useCallback(
        (exportOption: string) => {
            fireEvent(RIGHT_PANEL_MULTI_SELECT_EXPORT_OPTION_CLICK, {
                export_option: exportOption,
                selected_count: effectiveSelectedArtifactIds.length,
            });
        },
        [fireEvent, effectiveSelectedArtifactIds],
    );

    // Passthrough wrappers (the option-click events above are the source of truth for export analytics)
    const handleArtifactExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', run_id?: string, session_id?: string) => {
            return onArtifactExport?.(format, run_id, session_id);
        },
        [onArtifactExport],
    );

    const handleMultiSelectExport = useCallback(
        (format: 'docx' | 'xlsx' | 'pdf' | 'ppt', selectedIds: string[]) => {
            return onMultiSelectExport?.(format, selectedIds);
        },
        [onMultiSelectExport],
    );

    // Handle mouse down on divider
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    // Handle mouse move during resize
    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            if (!isResizing || !twoPanelContainerRef.current) return;

            const containerRect = twoPanelContainerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;

            // Calculate new width as percentage
            let newWidth = (mouseX / containerWidth) * 100;

            // Clamp to min/max
            newWidth = Math.max(minLeftPanelWidth, Math.min(maxLeftPanelWidth, newWidth));

            setLeftPanelWidth(newWidth);

            // Call callback if provided
            if (onPanelResize) {
                onPanelResize(newWidth);
            }
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isResizing, minLeftPanelWidth, maxLeftPanelWidth, onPanelResize]);

    // Handle touch events for mobile
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        const handleTouchMove = (e: TouchEvent) => {
            if (!isResizing || !twoPanelContainerRef.current) return;

            const containerRect = twoPanelContainerRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const touchX = e.touches[0].clientX - containerRect.left;

            let newWidth = (touchX / containerWidth) * 100;
            newWidth = Math.max(minLeftPanelWidth, Math.min(maxLeftPanelWidth, newWidth));

            setLeftPanelWidth(newWidth);

            if (onPanelResize) {
                onPanelResize(newWidth);
            }
        };

        const handleTouchEnd = () => {
            setIsResizing(false);
        };

        if (isResizing) {
            document.addEventListener('touchmove', handleTouchMove, { passive: false });
            document.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isResizing, minLeftPanelWidth, maxLeftPanelWidth, onPanelResize]);

    return (
        <ConfigProvider theme={theme}>
            <div
                className="relative flex w-full overflow-hidden bg-white"
                style={
                    footerHeight != null && footerHeight > 0
                        ? { height: `calc(100vh - ${footerHeight}px)` }
                        : { height: '100vh' }
                }
                data-test={dataTest}
                data-test-id={dataTestId}
                data-agentic-two-panel-layout
            >
                {/* Main Content Area */}
                <div ref={containerRef} className="flex flex-col flex-1 h-full overflow-hidden">
                    {/* Top Bar - hidden when disableNavbar (host app has its own navbar) */}
                    {!disableNavbar && (
                        <AgenticTopBar
                            breadcrumbs={breadcrumbs}
                            onBreadcrumbClick={onBreadcrumbClick}
                            onHomeClick={onHomeClick}
                            onNotificationClick={onNotificationClick}
                            showNotification={showNotification}
                            notificationCount={notificationCount}
                            onMenuToggle={
                                showSidebar
                                    ? handleMenuToggle
                                    : () => console.log('Menu toggle clicked (sidebar not enabled)')
                            }
                            viewMode={viewMode}
                            onViewModeChange={handleViewModeChange}
                            showViewModeToggle={showViewModeToggle}
                            onCommentClick={onCommentClick}
                            onCreateCaseClick={handleCreateCaseClick}
                            showCommentButton={showCommentButton}
                            showCreateCaseButton={showCreateCaseButton}
                            onDotsGridClick={onDotsGridClick}
                            showDotsGridButton={showDotsGridButton}
                            toolPanelOpen={toolPanelOpen}
                            onToolPanelOpenChange={onToolPanelOpenChange}
                            onExportClick={onExportClick}
                            onShareClick={onShareClick}
                            onPlaceholderClick={onPlaceholderClick}
                            onToolPanelSearch={onToolPanelSearch}
                            data-test-id={`${dataTestId}-top-bar`}
                        />
                    )}

                    {/* Two Panel Container */}
                    <div className="flex flex-1 overflow-hidden">
                        {/* Sidebar - part of flex layout when open */}
                        {showSidebar && !isSidebarCollapsed && (
                            <div className="flex-shrink-0 h-full" data-test-id={`${dataTestId}-sidebar`}>
                                <AgenticSidebar
                                    threads={sidebarThreads}
                                    threadsLoading={sidebarThreadsLoading}
                                    activeThreadId={sidebarActiveThreadId}
                                    user={sidebarUser}
                                    collapsed={false}
                                    onToggleSidebar={handleSidebarToggle}
                                    onNewChat={handleSidebarNewChat}
                                    onSearchChats={handleSidebarSearchChats}
                                    onSearchChatsClear={handleSidebarSearchChatsClear}
                                    onThreadSelect={handleSidebarThreadSelect}
                                    onThreadEdit={handleSidebarThreadEdit}
                                    onThreadDelete={handleSidebarThreadDelete}
                                    onUserClick={onSidebarUserClick}
                                    hideHeader={true}
                                    data-test-id={`${dataTestId}-sidebar-content`}
                                />
                            </div>
                        )}

                        {/* Two Panel Layout Container */}
                        <div ref={twoPanelContainerRef} className="flex flex-1 overflow-hidden relative">
                            {/* Left Panel - Collapsed in presentation mode, full in list mode */}
                            {viewMode === 'presentation' ? (
                                /* Focus Mode: When expanded, layout icon in top-right of left panel; when collapsed, vertical strip with icon */
                                <>
                                    {!focusModeLeftPanelExpanded && (
                                        <div
                                            className="flex-shrink-0 bg-white rounded-xl p-4 flex flex-col items-center gap-2 m-2 border border-[rgba(0,0,0,0.06)]"
                                            style={{ width: '52px' }}
                                            data-test-id={`${dataTestId}-collapsed-left-panel`}
                                        >
                                            {disableNavbar && showSidebar && (
                                                <Tooltip
                                                    title="View all threads"
                                                    placement="top"
                                                    getPopupContainer={() => document.body}
                                                >
                                                    <button
                                                        type="button"
                                                        className="flex items-center justify-center m-1 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                        onClick={handleMenuToggle}
                                                        aria-label="View all threads"
                                                        data-test-id={`${dataTestId}-sidebar-toggle-collapsed`}
                                                    >
                                                        <Menu size={20} color="rgba(0,0,0,0.65)" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <Tooltip
                                                title="Show the conversation panel"
                                                placement="right"
                                                getPopupContainer={() => document.body}
                                            >
                                                <button
                                                    type="button"
                                                    className="flex items-center justify-center m-2 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                    onClick={() => setFocusModeLeftPanelExpanded(true)}
                                                    aria-label="Show the conversation panel"
                                                    data-test-id={`${dataTestId}-expand-button`}
                                                >
                                                    <LayoutLeft size={20} color="rgba(0,0,0,0.65)" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    )}
                                    {focusModeLeftPanelExpanded && (
                                        <div
                                            className="relative flex-shrink-0 overflow-hidden min-w-0 p-2 bg-gray-100"
                                            style={{
                                                width: `${leftPanelWidth}%`,
                                                minWidth: `${minLeftPanelWidth}%`,
                                                maxWidth: `${maxLeftPanelWidth}%`,
                                            }}
                                            data-test-id={`${dataTestId}-focus-mode-left-panel`}
                                        >
                                            {/* Layout icon - top right of left panel (collapses panel when clicked). z-20 ensures it stays above panel content and remains visible when disableNavbar. */}
                                            <Tooltip
                                                title="Hide the conversation panel"
                                                placement="left"
                                                getPopupContainer={() => document.body}
                                            >
                                                <button
                                                    type="button"
                                                    className="absolute top-4 right-4 p-2 z-20 flex items-center justify-center bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                    onClick={handleHideLeftPanelInFocusMode}
                                                    aria-label="Hide the conversation panel"
                                                    data-test-id={`${dataTestId}-collapse-button`}
                                                >
                                                    <LayoutLeft size={20} color="rgba(0,0,0,0.65)" />
                                                </button>
                                            </Tooltip>
                                            <AgenticLeftPanel
                                                headerTitle={effectiveLeftPanelHeaderTitle}
                                                onHeaderActionClick={onLeftPanelHeaderAction}
                                                timestamp={effectiveLeftPanelTimestamp}
                                                showSidebarToggleInPanel={disableNavbar && showSidebar}
                                                onSidebarToggleInPanel={
                                                    disableNavbar && showSidebar ? handleMenuToggle : undefined
                                                }
                                                conversationMessages={effectiveConversationMessages}
                                                conversationPairs={effectiveConversationPairs}
                                                runningTasks={effectiveRunningTasks}
                                                plannerTasks={effectivePlannerTasks}
                                                phaseCards={effectivePhaseCards}
                                                onTogglePhase={onTogglePhase}
                                                onPhaseToggleCollapsed={handlePhaseToggleCollapsed}
                                                onSuggestedActionClick={handleSuggestedActionClick}
                                                hitlCards={hitlCards}
                                                showTasks={showTasks}
                                                onToggleTasks={onToggleTasks}
                                                onViewArtefact={handleViewArtefact}
                                                onEntityDisambiguationContinue={onEntityDisambiguationContinue}
                                                onEntityDisambiguationSkip={onEntityDisambiguationSkip}
                                                inputValue={layoutInputValue}
                                                onInputChange={handleLayoutInputChange}
                                                onInputSubmit={useMessagesMode ? handleInputSubmit : undefined}
                                                onQuestionBankClick={handleQuestionBankOpen}
                                                onSaveAsCustom={handleSaveAsCustomClick}
                                                inputDisabled={inputDisabled}
                                                isLoading={useMessagesMode && !!loadingMessage}
                                                searchDisclaimer={searchDisclaimer}
                                                lookupItems={lookupItems}
                                                selectedLookups={selectedLookups}
                                                onLookupsChange={onLookupsChange}
                                                onLoadLookups={onLoadLookups}
                                                lookupCategories={lookupCategories}
                                                lookupDimensionGroups={lookupDimensionGroups}
                                                onTrackEvent={onTrackEvent}
                                                analyticsThreadId={sidebarActiveThreadId}
                                                analyticsSessionId={analyticsSessionIdRef.current}
                                                data-test-id={`${dataTestId}-left-panel-content`}
                                            />
                                        </div>
                                    )}
                                </>
                            ) : (
                                /* List Mode: Left panel can be collapsed (narrow strip) or expanded (full panel with LayoutLeft toggle in top right) */
                                <>
                                    {listModeLeftPanelCollapsed ? (
                                        <div
                                            className="flex-shrink-0 bg-white rounded-xl p-4 flex flex-col items-center gap-2 m-2 border border-[rgba(0,0,0,0.06)]"
                                            style={{ width: '52px' }}
                                            data-test-id={`${dataTestId}-collapsed-left-panel-list`}
                                        >
                                            {disableNavbar && showSidebar && (
                                                <Tooltip
                                                    title="View all threads"
                                                    placement="top"
                                                    getPopupContainer={() => document.body}
                                                >
                                                    <button
                                                        type="button"
                                                        className="flex items-center justify-center m-1 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                        onClick={handleMenuToggle}
                                                        aria-label="View all threads"
                                                        data-test-id={`${dataTestId}-sidebar-toggle-collapsed-list`}
                                                    >
                                                        <Menu size={20} color="rgba(0,0,0,0.65)" />
                                                    </button>
                                                </Tooltip>
                                            )}
                                            <Tooltip
                                                title="Show the conversation panel"
                                                placement="right"
                                                getPopupContainer={() => document.body}
                                            >
                                                <button
                                                    type="button"
                                                    className="flex items-center justify-center m-2 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                    onClick={() => setListModeLeftPanelCollapsed(false)}
                                                    aria-label="Show the conversation panel"
                                                    data-test-id={`${dataTestId}-expand-button-list`}
                                                >
                                                    <LayoutLeft size={20} color="rgba(0,0,0,0.65)" />
                                                </button>
                                            </Tooltip>
                                        </div>
                                    ) : (
                                        <>
                                            <div
                                                className="relative flex-shrink-0 overflow-hidden min-w-0 p-2 bg-gray-100"
                                                style={{
                                                    width: `${leftPanelWidth}%`,
                                                    minWidth: `${minLeftPanelWidth}%`,
                                                    maxWidth: `${maxLeftPanelWidth}%`,
                                                }}
                                                data-test-id={`${dataTestId}-left-panel`}
                                            >
                                                {/* Layout icon - top right of left panel (collapses panel when clicked). Same as focus mode. */}
                                                <Tooltip
                                                    title="Hide the conversation panel"
                                                    placement="left"
                                                    getPopupContainer={() => document.body}
                                                >
                                                    <button
                                                        type="button"
                                                        className="absolute top-4 right-4 p-2 z-20 flex items-center justify-center bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                                                        onClick={handleHideLeftPanelInListMode}
                                                        aria-label="Hide the conversation panel"
                                                        data-test-id={`${dataTestId}-collapse-button-list`}
                                                    >
                                                        <LayoutLeft size={20} color="rgba(0,0,0,0.65)" />
                                                    </button>
                                                </Tooltip>
                                                <AgenticLeftPanel
                                                    headerTitle={effectiveLeftPanelHeaderTitle}
                                                    onHeaderActionClick={onLeftPanelHeaderAction}
                                                    timestamp={effectiveLeftPanelTimestamp}
                                                    showSidebarToggleInPanel={disableNavbar && showSidebar}
                                                    onSidebarToggleInPanel={
                                                        disableNavbar && showSidebar ? handleMenuToggle : undefined
                                                    }
                                                    conversationMessages={effectiveConversationMessages}
                                                    conversationPairs={effectiveConversationPairs}
                                                    runningTasks={effectiveRunningTasks}
                                                    plannerTasks={effectivePlannerTasks}
                                                    phaseCards={effectivePhaseCards}
                                                    onTogglePhase={onTogglePhase}
                                                    onPhaseToggleCollapsed={handlePhaseToggleCollapsed}
                                                    onSuggestedActionClick={handleSuggestedActionClick}
                                                    hitlCards={hitlCards}
                                                    showTasks={showTasks}
                                                    onToggleTasks={onToggleTasks}
                                                    onViewArtefact={handleViewArtefact}
                                                    onEntityDisambiguationContinue={onEntityDisambiguationContinue}
                                                    onEntityDisambiguationSkip={onEntityDisambiguationSkip}
                                                    inputValue={layoutInputValue}
                                                    onInputChange={handleLayoutInputChange}
                                                    onInputSubmit={useMessagesMode ? handleInputSubmit : undefined}
                                                    onQuestionBankClick={handleQuestionBankOpen}
                                                    onSaveAsCustom={handleSaveAsCustomClick}
                                                    inputDisabled={inputDisabled}
                                                    isLoading={useMessagesMode && !!loadingMessage}
                                                    searchDisclaimer={searchDisclaimer}
                                                    lookupItems={lookupItems}
                                                    selectedLookups={selectedLookups}
                                                    onLookupsChange={onLookupsChange}
                                                    onLoadLookups={onLoadLookups}
                                                    lookupCategories={lookupCategories}
                                                    lookupDimensionGroups={lookupDimensionGroups}
                                                    onTrackEvent={onTrackEvent}
                                                    analyticsThreadId={sidebarActiveThreadId}
                                                    analyticsSessionId={analyticsSessionIdRef.current}
                                                    data-test-id={`${dataTestId}-left-panel-content`}
                                                />
                                            </div>

                                            {/* Resizable Divider - Only shown when left panel is expanded in list mode */}
                                            <Tooltip
                                                title="Drag to resize panels"
                                                placement="left"
                                                getPopupContainer={() => document.body}
                                            >
                                                <div
                                                    ref={dividerRef}
                                                    onMouseDown={handleMouseDown}
                                                    onTouchStart={handleTouchStart}
                                                    role="separator"
                                                    aria-label="Drag to resize panels"
                                                    className={`flex-shrink-0 w-1 bg-[rgba(159,144,144,0.06)] hover:bg-[rgba(0,0,0,0.12)] active:bg-[#7C3AED] cursor-col-resize transition-colors relative z-20 touch-none ${
                                                        isResizing ? 'bg-[#7C3AED]' : ''
                                                    }`}
                                                    data-test-id={`${dataTestId}-divider`}
                                                    style={{
                                                        touchAction: 'none',
                                                    }}
                                                >
                                                    {/* Dragger Handle */}
                                                    <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 flex items-center justify-center pointer-events-none">
                                                        <div className="w-4 h-10 bg-white rounded-[4px] shadow-[0_1px_2px_rgba(0,0,0,0.1)] flex flex-col items-center justify-center gap-[3px]">
                                                            {/* Three horizontal bars */}
                                                            <div className="w-1 h-0.5 bg-[rgb(48,48,48)] rounded-full" />
                                                            <div className="w-1 h-0.5 bg-[rgb(48,48,48)] rounded-full" />
                                                            <div className="w-1 h-0.5 bg-[rgb(48,48,48)] rounded-full" />
                                                        </div>
                                                    </div>
                                                </div>
                                            </Tooltip>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Right Panel */}
                            <div
                                ref={rightPanelRef}
                                className="flex-1 h-full min-h-0 overflow-hidden min-w-0 p-2 bg-gray-100"
                                style={
                                    viewMode === 'list' && !listModeLeftPanelCollapsed
                                        ? { width: `${100 - leftPanelWidth}%` }
                                        : undefined
                                }
                                data-test-id={`${dataTestId}-right-panel`}
                            >
                                {viewMode === 'presentation' ? (
                                    <AgenticFocusRightPanel
                                        items={flattenedItems}
                                        activeIndex={focusPanelActiveIndex}
                                        onActiveIndexChange={handlePresentationActiveIndexChange}
                                        onClose={onRightPanelClose}
                                        onExport={handleArtifactExport}
                                        onCreateCase={handleCreateCaseClick}
                                        showFocusModeToggleInPanel={disableNavbar}
                                        onViewModeChangeInPanel={disableNavbar ? handleViewModeChange : undefined}
                                        onMethodologyExpandChange={handleMethodologyExpandChange}
                                        onArtefactMenuTriggerClick={handleArtefactMenuTriggerClick}
                                        onArtefactExportOptionClick={handleArtefactExportOptionClick}
                                        onTrackChartEvent={chartEventForwarder}
                                        onSummaryCitationClick={handleSummaryCitationClick}
                                        data-test-id={`${dataTestId}-focus-right-panel`}
                                    />
                                ) : (
                                    <AgenticRightPanel
                                        rightPanelBlocks={effectiveRightPanelBlocks}
                                        accordionItems={effectiveAccordionItems}
                                        artifacts={artifacts}
                                        activeArtifactId={activeArtifactId}
                                        summary={effectiveSummary}
                                        onArtifactSelect={onArtifactSelect}
                                        onArtifactClose={onArtifactClose}
                                        isMultiSelectMode={effectiveIsMultiSelectMode}
                                        onToggleMultiSelect={handleToggleMultiSelect}
                                        selectedArtifactIds={effectiveSelectedArtifactIds}
                                        onArtifactSelectionChange={handleArtifactSelectionChange}
                                        onSelectAll={handleSelectAllArtifacts}
                                        onDeselectAll={handleDeselectAllArtifacts}
                                        onMethodologyExpandChange={handleMethodologyExpandChange}
                                        onArtefactMenuTriggerClick={handleArtefactMenuTriggerClick}
                                        onArtefactExportOptionClick={handleArtefactExportOptionClick}
                                        onMultiSelectExportOptionClick={handleMultiSelectExportOptionClick}
                                        onTrackChartEvent={chartEventForwarder}
                                        onClose={onRightPanelClose}
                                        onArtifactExport={handleArtifactExport}
                                        onMultiSelectExport={handleMultiSelectExport}
                                        onCreateCase={handleCreateCaseClick}
                                        showFocusModeToggleInPanel={disableNavbar}
                                        viewMode={viewMode}
                                        onViewModeChangeInPanel={disableNavbar ? handleViewModeChange : undefined}
                                        conversationPairCount={
                                            effectiveConversationPairs?.length ?? conversationMessages?.length ?? 0
                                        }
                                        conversationTitle={effectiveLeftPanelHeaderTitle}
                                        exportDeepLink={exportDeepLink}
                                        threadId={exportThreadId ?? sidebarActiveThreadId}
                                        deepLinkTaskId={activeDeepLinkTaskId}
                                        onDeepLinkTaskScrollComplete={handleDeepLinkTaskScrollComplete}
                                        deepLinkRunId={activeDeepLinkRunId}
                                        onDeepLinkRunScrollComplete={handleDeepLinkRunScrollComplete}
                                        onSummaryCitationClick={handleSummaryCitationClick}
                                        data-test-id={`${dataTestId}-right-panel-content`}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Create Case Modal */}
                <CreateCaseModal
                    open={createCaseModalOpen}
                    onCancel={handleCaseModalCancel}
                    onSave={handleCaseSave}
                    existingCases={existingCases}
                    data-test-id={`${dataTestId}-create-case-modal`}
                />

                {/* Question Bank Modal (same as AgenticMainLayout / AgenticLandingContent) */}
                <Modal
                    open={isQuestionBankOpen}
                    onCancel={() => setIsQuestionBankOpen(false)}
                    footer={null}
                    width={1600}
                    centered
                    closable={false}
                    maskClosable={true}
                    className="question-bank-modal w-[80%]"
                    data-test-id={`${dataTestId}-question-bank-modal`}
                >
                    <div className="h-[80vh]">
                        <AgenticQuestionBank
                            categories={
                                questionBankCategories?.length
                                    ? transformQuestionBankCategories(questionBankCategories)
                                    : undefined
                            }
                            savedQuestions={effectiveSavedQuestions}
                            onClose={() => setIsQuestionBankOpen(false)}
                            onQuestionSelect={handleQuestionSelect}
                            onSectionClick={handleQuestionBankSectionClick}
                            onEditSavedQuestion={handleEditSavedQuestion}
                            onDeleteSavedQuestion={handleDeleteSavedQuestion}
                            onSearch={handleQuestionBankSearch}
                            onSearchClear={handleQuestionBankSearchClear}
                            data-test-id={`${dataTestId}-question-bank`}
                        />
                    </div>
                </Modal>
            </div>
        </ConfigProvider>
    );
};

export default AgenticTwoPanelLayout;
